/**
 * ArtifactStreamingBox Component
 *
 * Box que mostra o artifact sendo gerado em tempo real
 * Aparece abaixo da mensagem durante o streaming
 */

import React, { useEffect, useRef, memo } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface ArtifactStreamingBoxProps {
  title: string;
  type: string;
  content: string;
  language?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const getTypeLabel = (type: string, language?: string): string => {
  switch (type) {
    case 'text/html':
      return 'HTML';
    case 'application/vnd.ant.code':
      return language?.toUpperCase() || 'Code';
    case 'text/markdown':
      return 'Markdown';
    default:
      return 'Content';
  }
};

// =============================================================================
// ICONS
// =============================================================================

const DocumentIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z"
      fill="currentColor"
      opacity="0.3"
    />
    <path d="M14 2l6 6h-6V2z" fill="currentColor" />
  </svg>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const ArtifactStreamingBox: React.FC<ArtifactStreamingBoxProps> = memo(
  ({ title, type, content, language }) => {
    const contentRef = useRef<HTMLPreElement>(null);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'auto',
        });
      }
    }, [content]);

    return (
      <div className="rounded-lg border border-white/10 bg-[#1a1a1a] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-[#252525]">
          <div className="flex items-center gap-2">
            <DocumentIcon />
            <span className="text-sm font-medium text-white truncate max-w-[200px]">
              {title}
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
              {getTypeLabel(type, language)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="w-3 h-3 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
            <span>Gerando...</span>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[300px] overflow-hidden">
          <pre
            ref={contentRef}
            className="m-0 p-3 bg-[#1e1e1e] text-[#d4d4d4] text-sm font-mono overflow-auto max-h-[300px] whitespace-pre-wrap break-words"
          >
            <code>{content}</code>
          </pre>
        </div>
      </div>
    );
  }
);

ArtifactStreamingBox.displayName = 'ArtifactStreamingBox';

export default ArtifactStreamingBox;
